"use client";

import { useEffect } from "react";
import { captureError } from "@/lib/error/handler";

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Global Error Page (NFR-002)
 *
 * Handles uncaught errors at the app router level.
 * Displays user-friendly error message and recovery options.
 */
export default function ErrorPage({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    // Log the error to our centralized handler
    captureError(error, {
      source: "GlobalErrorPage",
      extra: {
        digest: error.digest,
      },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md text-center">
        <div className="mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Something went wrong
          </h1>
          <p className="text-gray-600">
            We apologize for the inconvenience. An error occurred while
            processing your request.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={reset}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Go to home
          </button>
        </div>

        {process.env.NODE_ENV === "development" && error.message && (
          <div className="mt-6 rounded-lg bg-gray-100 p-4 text-left">
            <p className="mb-1 text-xs font-medium text-gray-500">
              Error details (dev only):
            </p>
            <p className="break-all font-mono text-xs text-gray-700">
              {error.message}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
